import {isDarkMode} from 'app/pages/school/Schedule/components/timetable/commons/utils'
import {USER_TYPES} from 'common/constants'
import {MessageSquare, Sparkles, X} from 'lucide-react'
import {type FormEvent, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {toast} from 'react-toastify'
import {RootState, UserReducer} from 'setup'
import {
  getApplicationFeedbackStatus,
  submitApplicationFeedback,
} from 'setup/redux/Actions/ApplicationFeedback/applicationFeedback'

import {markFeedbackSurveyClosed, markFeedbackSurveyOpened} from './feedbackSurveyState'

const FEEDBACK_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)'

// Eligible user types for the monthly application feedback popup.
const ELIGIBLE_USER_TYPES = new Set<string>([USER_TYPES.ADMIN, USER_TYPES.STAFF])

const getDismissalKey = (token: string) => `feedback_survey_dismissed_${token.slice(0, 20)}`

const isSurveyDismissed = (token: string): boolean => {
  try {
    return sessionStorage.getItem(getDismissalKey(token)) === '1'
  } catch {
    return false
  }
}

const setSurveyDismissed = (token: string): void => {
  try {
    sessionStorage.setItem(getDismissalKey(token), '1')
  } catch {
    // ignore storage errors
  }
}

type StatusResult = {
  shouldShow: boolean
  submitted: boolean
  applicationId: number | null
  periodMonth: string | null
}

type ApiResponse<T> = {
  statusCode: number
  message?: string
  status?: string | boolean
  result?: T
}

const StarRow = ({
  value,
  onChange,
  ariaLabel,
}: {
  value: number
  onChange: (n: number) => void
  ariaLabel: string
}) => {
  const intl = useIntl()
  const [hover, setHover] = useState(0)
  const display = hover || value
  return (
    <div className='d-flex align-items-center' aria-label={ariaLabel}>
      <div className='d-inline-flex align-items-center' style={{gap: 6}}>
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= display
          return (
            <button
              key={n}
              type='button'
              className='feedback-popup-star'
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => onChange(n)}
              aria-label={intl.formatMessage({id: 'FEEDBACK_SURVEY.STAR_ARIA'}, {n})}
              style={{
                background: 'transparent',
                border: 0,
                padding: 2,
                lineHeight: 0,
                cursor: 'pointer',
              }}
            >
              <i
                className='bi bi-star-fill'
                style={{
                  fontSize: 28,
                  color: filled ? '#FFB400' : '#E4E6EF',
                  textShadow: filled ? '0 1px 2px rgba(255, 180, 0, 0.35)' : 'none',
                  transition: 'color 0.15s ease',
                }}
              />
            </button>
          )
        })}
      </div>
      <span className='ms-3 text-muted fs-7 fw-semibold'>
        <FormattedMessage id='FEEDBACK_SURVEY.RATING_VALUE' values={{value: display}} />
      </span>
    </div>
  )
}

const FeedbackSurveyPopup = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const userReducer = useSelector<RootState>(
    ({userReducer}) => userReducer,
    shallowEqual
  ) as UserReducer

  const accessToken = userReducer.accessToken || ''
  const userType = userReducer.userType
  const isEligible = ELIGIBLE_USER_TYPES.has(userType)

  const [open, setOpen] = useState(false)
  const [isDark, setIsDark] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [productRating, setProductRating] = useState(5)
  const [supportRating, setSupportRating] = useState(5)
  const [comments, setComments] = useState('')

  // Track dark mode the same way the dashboard banner modal does so the
  // right-side form matches the rest of the app chrome.
  useEffect(() => {
    const checkDarkMode = () => setIsDark(isDarkMode() ?? false)
    checkDarkMode()
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme', 'class'],
    })
    return () => observer.disconnect()
  }, [])

  // Broadcast open/close so other modals (e.g. the dashboard banner auto-open)
  // can stay out of the way while this popup is on screen.
  useEffect(() => {
    if (open) {
      markFeedbackSurveyOpened()
    } else {
      markFeedbackSurveyClosed()
    }
  }, [open])

  useEffect(() => {
    return () => {
      // Component unmount — ensure any consumers see the popup as closed.
      markFeedbackSurveyClosed()
    }
  }, [])

  // Fetch the monthly status once the user is authenticated and eligible.
  useEffect(() => {
    if (!accessToken || !isEligible) return

    const onSuccess = (response: ApiResponse<StatusResult>) => {
      if (!response?.result?.shouldShow) return
      if (isSurveyDismissed(accessToken)) return
      setOpen(true)
    }

    const onFailure = () => {
      // Silently ignore — the popup must never block the app.
    }

    dispatch(getApplicationFeedbackStatus(onSuccess, onFailure))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isEligible])

  if (!isEligible || !accessToken) return null

  const handleClose = () => {
    if (submitting) return
    setSurveyDismissed(accessToken)
    setOpen(false)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)

    const payload = {
      productRating,
      supportRating,
      comments: comments.trim() || undefined,
    }

    const onSuccess = () => {
      setSubmitting(false)
      setOpen(false)
      toast.success(intl.formatMessage({id: 'FEEDBACK_SURVEY.SUBMIT_SUCCESS'}))
    }

    const onFailure = (response: ApiResponse<unknown>) => {
      setSubmitting(false)
      const message =
        response?.message || intl.formatMessage({id: 'FEEDBACK_SURVEY.SUBMIT_FAILURE'})
      toast.error(message)
    }

    dispatch(submitApplicationFeedback(payload, onSuccess, onFailure))
  }

  return (
    <Modal
      show={open}
      onHide={handleClose}
      size='lg'
      centered
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
      contentClassName={`border-0 overflow-hidden ai-timetable-modal ${isDark ? 'bg-dark' : ''}`}
      dialogClassName='modal-dialog-centered'
      aria-label={intl.formatMessage({id: 'FEEDBACK_SURVEY.DIALOG_ARIA'})}
    >
      <div className='position-relative modal-content-wrapper'>
        {/* Close Button - Absolute Positioned */}
        <button
          type='button'
          onClick={handleClose}
          disabled={submitting}
          className={`position-absolute top-0 end-0 m-3 btn btn-icon btn-sm rounded-circle d-flex align-items-center justify-content-center modal-close-btn ${
            isDark ? 'dark-mode' : 'light-mode'
          }`}
          aria-label={intl.formatMessage({id: 'GENERAL.CLOSE'})}
        >
          <X size={20} className={isDark ? 'text-white' : 'text-dark'} />
        </button>

        <form onSubmit={handleSubmit}>
          <div className='row g-0'>
            {/* Left Side: Visual / Header */}
            <div
              className='col-12 col-md-5 text-white d-flex flex-column p-5 modal-left-panel'
              style={{
                background: FEEDBACK_GRADIENT,
                backgroundSize: '200% 200%',
              }}
            >
              {/* Animated Background Pattern */}
              <div className='animated-background' />

              {/* Decorative Circles */}
              <div className='decorative-circle-1' />
              <div className='decorative-circle-2' />

              {/* Icon */}
              <div className='mb-4 icon-container'>
                <div className='bg-white bg-opacity-20 d-inline-flex align-items-center justify-content-center rounded-4 border border-white border-opacity-30 icon-wrapper'>
                  <MessageSquare size={48} strokeWidth={1.75} className='text-white' />
                </div>
              </div>

              {/* Badge & Title */}
              <div className='flex-grow-1 d-flex flex-column justify-content-center content-wrapper'>
                <div className='d-inline-flex align-items-center gap-2 mb-3 px-3 py-2 rounded-pill new-feature-badge'>
                  <Sparkles size={14} strokeWidth={2.5} />{' '}
                  <FormattedMessage id='FEEDBACK_SURVEY.TITLE' />
                </div>
                <h2 className='fw-bold mb-3 text-white title'>
                  <FormattedMessage id='FEEDBACK_SURVEY.TITLE' />
                </h2>
                <p className='text-white description'>
                  <FormattedMessage id='FEEDBACK_SURVEY.INTRO' />
                </p>
              </div>
            </div>

            {/* Right Side: Form */}
            <div
              className={`col-12 col-md-7 p-5 d-flex flex-column modal-right-panel ${
                isDark ? 'dark-mode' : 'light-mode'
              }`}
            >
              {/* Decorative gradient line */}
              <div
                className='gradient-line'
                style={{
                  background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 100%)',
                }}
              />

              <div className='content-section mb-4'>
                <div className='mb-4'>
                  <label className='form-label fs-7 fw-bold text-gray-800 mb-2'>
                    <FormattedMessage id='FEEDBACK_SURVEY.PRODUCT_RATING_LABEL' />
                  </label>
                  <StarRow
                    value={productRating}
                    onChange={setProductRating}
                    ariaLabel={intl.formatMessage({id: 'FEEDBACK_SURVEY.PRODUCT_RATING_ARIA'})}
                  />
                </div>

                <div className='mb-4'>
                  <label className='form-label fs-7 fw-bold text-gray-800 mb-2'>
                    <FormattedMessage id='FEEDBACK_SURVEY.SUPPORT_RATING_LABEL' />
                  </label>
                  <StarRow
                    value={supportRating}
                    onChange={setSupportRating}
                    ariaLabel={intl.formatMessage({id: 'FEEDBACK_SURVEY.SUPPORT_RATING_ARIA'})}
                  />
                </div>

                <div className='mb-2'>
                  <label className='form-label fs-7 fw-bold text-gray-800 mb-2'>
                    <FormattedMessage id='FEEDBACK_SURVEY.COMMENTS_LABEL' />
                  </label>
                  <textarea
                    className='form-control form-control-solid'
                    rows={3}
                    maxLength={5000}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'FEEDBACK_SURVEY.COMMENTS_PLACEHOLDER',
                    })}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className='mt-auto'>
                <button
                  type='submit'
                  className='btn btn-lg w-100 text-white fw-bold d-flex align-items-center justify-content-center gap-2 rounded-pill try-now-btn'
                  style={{
                    background: FEEDBACK_GRADIENT,
                    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                  }}
                  disabled={submitting}
                >
                  <span className='shimmer-effect' />
                  <FormattedMessage id={submitting ? 'GENERAL.SUBMITTING' : 'GENERAL.SUBMIT'} />
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default FeedbackSurveyPopup
